import { useState, useMemo } from "react";
import { collection, addDoc, doc, updateDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { useFirestoreCollection } from "@/hooks/useFirestore";
import { Card } from "@/components/ui/Card";
import { Avatar } from "@/components/ui/Avatar";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { EmptyState } from "@/components/ui/EmptyState";
import { MemberPicker } from "@/components/ui/MemberPicker";
import { CreateChoreModal } from "@/components/features/CreateChoreModal";
import { Link } from "react-router-dom";
import { useTranslations } from "@/contexts/I18nContext";
import { fromTimestamp } from "@/lib/firestore";
import { choreCategoryMeta } from "@/lib/choreCategory";
import {
  isDoneNow,
  toggleChoreCompletion,
  filterAssignedChoresByHousehold,
} from "@/lib/chores";
import { buildAssignedChoreDoc } from "@/lib/assignChore";
import type { HouseholdChore, AssignedChore, MemberProfile } from "@/lib/models";

export function ChoresPage() {
  const { householdId, members, profile, loading: ctxLoading } = useHouseholdContext();
  const t = useTranslations();

  const { data: chores, loading: choresLoading } = useFirestoreCollection<HouseholdChore>(
    householdId ? `households/${householdId}/chores` : null
  );

  // Fetch assigned chores for all members — we listen per-member
  const [filterMemberId, setFilterMemberId] = useState<string | null>(null);
  const [dayOffset, setDayOffset] = useState<number>(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [assignModalChore, setAssignModalChore] = useState<HouseholdChore | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // For the assigned section, listen to the filtered member's assigned chores
  const targetMemberId = filterMemberId ?? profile?.uid ?? null;
  const { data: assignedChoresRaw, loading: assignedLoading } = useFirestoreCollection<AssignedChore>(
    targetMemberId ? `member_profiles/${targetMemberId}/assigned_chores` : null
  );

  // The chores screen is inherently household-scoped — switching
  // households swaps the chore list context. Filter the raw
  // cross-household subcollection by the active household so the
  // displayed list matches the scope of the (future) chore_completions
  // listener + wallet aggregator. Mirrors iOS PR #111 + Android
  // `feat/android-rewards-firestore` commit 68292ee. See
  // [[tasks/daily-chore-reset]].
  const assignedChores = useMemo(
    () => filterAssignedChoresByHousehold(assignedChoresRaw, householdId),
    [assignedChoresRaw, householdId],
  );

  const loading = ctxLoading || choresLoading;

  const memberMap = useMemo(() => {
    const map: Record<string, MemberProfile> = {};
    for (const m of members) {
      if (m.uid) map[m.uid] = m;
    }
    return map;
  }, [members]);

  // Filter assigned chores by the selected day offset (0 = today, 1 =
  // tomorrow, ...). Mirrors iOS AssignedChoresListView date stepper.
  const dayLabel = useMemo(() => {
    if (dayOffset === 0) return t("common.today");
    if (dayOffset === 1) return t("common.tomorrow");
    if (dayOffset === -1) return t("common.yesterday");
    const target = new Date();
    target.setDate(target.getDate() + dayOffset);
    return target.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
  }, [dayOffset, t]);

  const filteredAssignedChores = useMemo(() => {
    const target = new Date();
    target.setDate(target.getDate() + dayOffset);
    target.setHours(0, 0, 0, 0);
    const next = new Date(target.getTime() + 24 * 60 * 60 * 1000);
    return assignedChores.filter((ac) => {
      const due = ac.due_date as { seconds?: number; toDate?: () => Date } | undefined;
      if (!due) return dayOffset === 0;
      const d = typeof due.toDate === "function"
        ? due.toDate()
        : due.seconds != null
          ? new Date(due.seconds * 1000)
          : null;
      if (!d) return dayOffset === 0;
      return d >= target && d < next;
    });
  }, [assignedChores, dayOffset]);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  async function handleAssign(member: MemberProfile) {
    if (!assignModalChore || !member.uid || !profile?.uid || !householdId) return;

    // Create assigned chore for the member. Body shape lives in
    // `lib/assignChore.ts` so it's unit-testable and the field-copy
    // contract is pinned (see `assignChore.test.ts`).
    await addDoc(
      collection(db, `member_profiles/${member.uid}/assigned_chores`),
      buildAssignedChoreDoc({
        chore: assignModalChore,
        householdId,
        assignedBy: profile.uid,
      }),
    );

    // Update the chore's assignees array
    if (assignModalChore.uid) {
      const choreRef = doc(db, `households/${householdId}/chores`, assignModalChore.uid);
      const existing = assignModalChore.assignees ?? [];
      if (!existing.includes(member.uid)) {
        await updateDoc(choreRef, { assignees: [...existing, member.uid] });
      }
    }

    const name = member.nickname ?? member.name ?? "them";
    showToast(t("chores.assignToast", { name }));
    setAssignModalChore(null);
  }

  async function handleToggleComplete(chore: AssignedChore) {
    if (!chore.uid || !targetMemberId || !profile?.uid) return;
    // assigneeUid = whose chore it is (could be a kid if an adult is filtered to them)
    // completerUid = who's tapping right now (the adult, in the adult-marks-kid case)
    await toggleChoreCompletion({
      chore,
      assigneeUid: targetMemberId,
      completerUid: profile.uid,
    });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: "var(--accent-color, #00C7BE)", borderTopColor: "transparent" }}
        />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white px-4 py-2 rounded-lg shadow-lg text-sm animate-fade-in">
          {toast}
        </div>
      )}

      {/* Page title + choreboard link */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("chores.title")}</h1>
        <Link
          to="/choreboard"
          className="text-sm font-medium px-3 py-1.5 rounded-lg transition-colors hover:opacity-80"
          style={{ color: "var(--accent-color)" }}
        >
          {t("chores.choreboard")}
        </Link>
      </div>

      {/* New chore button — opens full modal capturing iOS-canonical fields */}
      <button
        type="button"
        onClick={() => setShowCreateModal(true)}
        className="w-full py-2.5 rounded-lg text-sm font-semibold text-white bg-[color:var(--accent-color)] hover:opacity-90 transition-opacity"
      >
        {t("chores.create.newChoreButton")}
      </button>

      {/* Household chores list */}
      <SectionHeader title={t("chores.householdChores")} count={chores.length} icon="🧹" />

      {chores.length === 0 ? (
        <EmptyState
          icon="🧹"
          title={t("chores.empty.title")}
          subtitle={t("chores.empty.subtitle")}
        />
      ) : (
        <div className="space-y-2">
          {chores.map((chore) => (
            <Card key={chore.uid}>
              <div className="flex items-start gap-3">
                {/* Category icon — mirrors iOS ChoresView category tint */}
                {(() => {
                  const meta = choreCategoryMeta(chore.category);
                  return (
                    <div
                      className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-base text-white ${meta.color}`}
                      aria-hidden="true"
                    >
                      {meta.icon}
                    </div>
                  );
                })()}

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium truncate">{chore.title}</p>
                    <button
                      onClick={() => setAssignModalChore(chore)}
                      className="shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                    >
                      {t("chores.assign")}
                    </button>
                  </div>
                  {chore.description && (
                    <p className="text-sm text-gray-500 truncate mt-0.5">{chore.description}</p>
                  )}

                  {/* Recurrence + due date + points pills */}
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    {chore.recurrence_type && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300">
                        {t(`chores.recurrence.${chore.recurrence_type.toLowerCase()}`)}
                      </span>
                    )}
                    {(() => {
                      const due = fromTimestamp(chore.due_date);
                      if (!due) return null;
                      return (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                          {due.toLocaleDateString(undefined, {
                            month: "short",
                            day: "numeric",
                          })}
                        </span>
                      );
                    })()}
                    {chore.points != null && chore.points > 0 && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                        {t(chore.points === 1 ? "chores.points.one" : "chores.points.other", { count: chore.points })}
                      </span>
                    )}
                  </div>

                  {/* Assignee avatars */}
                  {chore.assignees && chore.assignees.length > 0 && (
                    <div className="flex -space-x-1 mt-1.5">
                      {chore.assignees.map((id) => {
                        const m = memberMap[id];
                        return (
                          <Avatar
                            key={id}
                            src={m?.profile_url}
                            alt={m?.nickname ?? m?.name ?? ""}
                            size={24}
                            className="ring-2 ring-white dark:ring-gray-900"
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Assigned chores section */}
      <SectionHeader title={t("chores.assigned")} count={filteredAssignedChores.length} icon="✨" />

      {/* Day stepper */}
      <div className="flex items-center justify-between px-1">
        <button
          type="button"
          onClick={() => setDayOffset((v) => v - 1)}
          aria-label={t("dashboard.events.previousDay")}
          className="p-1.5 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-white/10"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <span className="text-sm font-semibold">{dayLabel}</span>
        <button
          type="button"
          onClick={() => setDayOffset((v) => v + 1)}
          aria-label={t("dashboard.events.nextDay")}
          className="p-1.5 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-white/10"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>

      {/* Member filter tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1 px-1">
        {members.map((m) => (
          <button
            key={m.uid}
            onClick={() => setFilterMemberId(m.uid ?? null)}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              targetMemberId === m.uid
                ? "bg-[color:var(--accent-color)] text-white"
                : "bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300"
            }`}
          >
            {m.nickname ?? m.name ?? t("members.detail.unknownMember")}
          </button>
        ))}
      </div>

      {assignedLoading ? (
        <div className="flex justify-center py-8">
          <div
            className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: "var(--accent-color, #00C7BE)", borderTopColor: "transparent" }}
          />
        </div>
      ) : filteredAssignedChores.length === 0 ? (
        <EmptyState
          icon="✨"
          title={t("chores.assignedEmpty.title")}
          subtitle={t("chores.assignedEmpty.subtitle")}
        />
      ) : (
        <div className="space-y-2">
          {filteredAssignedChores.map((ac) => (
            <Card key={ac.uid}>
              <button
                onClick={() => handleToggleComplete(ac)}
                className="flex items-center gap-3 w-full text-left"
              >
                <div
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors shrink-0 ${
                    isDoneNow(ac)
                      ? "bg-[color:var(--accent-color)] border-[color:var(--accent-color)]"
                      : "border-gray-300"
                  }`}
                >
                  {isDoneNow(ac) && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
                <span className={`flex-1 ${isDoneNow(ac) ? "line-through text-gray-400" : ""}`}>
                  {ac.title}
                </span>
                {ac.points != null && ac.points > 0 && (
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 shrink-0">
                    {t(ac.points === 1 ? "chores.points.one" : "chores.points.other", { count: ac.points })}
                  </span>
                )}
              </button>
            </Card>
          ))}
        </div>
      )}

      {/* Create chore modal */}
      {showCreateModal && (
        <CreateChoreModal onClose={() => setShowCreateModal(false)} />
      )}

      {/* Assign modal */}
      {assignModalChore && (
        <div className="fixed inset-0 z-40 flex items-end sm:items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setAssignModalChore(null)}
          />
          <div className="relative z-50 w-full max-w-sm mx-4 mb-4 sm:mb-0 bg-white rounded-2xl shadow-xl overflow-hidden text-gray-900">
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-semibold text-center">
                {t("chores.assignDialog.title", { title: assignModalChore.title })}
              </h3>
            </div>
            <MemberPicker members={members} onSelect={handleAssign} />
            <div className="p-3 border-t border-gray-100">
              <button
                onClick={() => setAssignModalChore(null)}
                className="w-full py-2 text-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                {t("common.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
